import * as Dialog from "@radix-ui/react-dialog";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { Button } from "./ui/Button";

interface DeleteEntityDialogProps {
  entityName: string;
  entityId: string;
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

export function DeleteEntityDialog({
  entityName,
  entityId,
  open,
  onClose,
  onDeleted,
}: DeleteEntityDialogProps): JSX.Element {
  const [typed, setTyped] = useState("");
  const queryClient = useQueryClient();
  const { t } = useTranslation(["entities", "common"]);

  const mutation = useMutation({
    mutationFn: () => api.delete(`/entities/${entityId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["entities"] });
      void queryClient.invalidateQueries({ queryKey: ["entities-all"] });
      void queryClient.invalidateQueries({ queryKey: ["renewals"] });
      void queryClient.invalidateQueries({ queryKey: ["deleted-entities"] });
      toast.success(t("delete_success"));
      setTyped("");
      onDeleted();
    },
    onError: (err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        toast.error(t("delete_already_removed"));
      } else {
        toast.error(t("delete_error"));
      }
    },
  });

  const confirmed = typed.trim() === entityName.trim();

  function handleClose() {
    setTyped("");
    onClose();
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(v) => {
        if (!v) handleClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50
                     bg-surface rounded-[11px] border border-[#fbcdcd] shadow-pop
                     p-6 w-[min(92vw,420px)] focus:outline-none"
          aria-describedby="delete-entity-desc"
        >
          <Dialog.Title className="font-display font-semibold text-[15px] tracking-[-0.01em] text-text mb-1">
            {t("delete_dialog_title", { name: entityName })}
          </Dialog.Title>
          <Dialog.Description id="delete-entity-desc" className="text-[13px] text-muted mb-4">
            {t("delete_dialog_desc")}
          </Dialog.Description>

          <input
            className="w-full border border-border rounded-lg px-3 py-2 text-base
                       bg-surface-2 text-text placeholder:text-dim
                       focus:bg-surface focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/14
                       mb-4"
            placeholder={t("delete_dialog_placeholder")}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            aria-label={t("delete_dialog_title", { name: entityName })}
          />

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={handleClose}>
              {t("common:cancel")}
            </Button>
            <Button
              variant="danger-soft"
              disabled={!confirmed || mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {mutation.isPending ? t("entities:applying_label") : t("common:delete")}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
